import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/Field";
import { Checkbox } from "@/components/ui/checkbox";
import { PaymentMethodPicker, usePaymentConfig } from "@/components/PaymentMethod";
import { CouponBox, CouponCounters, useCouponStats } from "@/components/admission/CouponBox";
import { BillSummary } from "@/components/admission/BillSummary";
import { RazorpayPay } from "@/components/admission/RazorpayPay";
import { api, fmtINR } from "@/lib/api";
import { IMG } from "@/lib/content";
import { errText } from "@/lib/aadhaar";
import { TAGLINE, SUPPORT_LINE, CLASS_FEES, OFFER_END_LABEL, KITS_NOTE, feeFor, sectionFor, takeRef, payable } from "@/lib/offer";
import { Gift } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const PROGRAMS = ["AI", "Robotics", "UAV", "Aerospace"];
const PROGRAM_HI = { AI: "AI", Robotics: "रोबोटिक्स", UAV: "UAV / ड्रोन", Aerospace: "एयरोस्पेस" };
const CLASSES = ["6", "7", "8", "9", "10", "11", "12"];

const Pick = ({ id, label, value, onChange, options }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-semibold text-[#0A192F]">{label}<span className="text-[#FF2E63]"> *</span></Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-11 bg-white" data-testid={`select-${id}`}><SelectValue placeholder="Select" /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.v} value={o.v} data-testid={`option-${id}-${o.v}`}>{o.l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const FeeCards = ({ cls, onPick }) => {
  const { L } = useLang();
  return (
    <div className="grid sm:grid-cols-3 gap-3" data-testid="fee-cards">
      {CLASS_FEES.map((f, i) => (
        <button type="button" key={f.fee} onClick={() => onPick(f.classes[0])} data-testid={`fee-card-${i}`}
          className={`text-left p-4 rounded-xl border-2 bg-white transition-[border-color,box-shadow] ${f.classes.includes(cls) ? "border-[#FF2E63] shadow-lg" : "border-slate-200 hover:border-slate-400"}`}>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">{L(...f.label)}</div>
          <div className="text-2xl font-black text-[#0A192F] font-['Outfit'] mt-1">{fmtINR(f.fee)}<span className="text-xs font-bold text-slate-400">/{L("year", "वर्ष")}</span></div>
          <div className="text-[11px] font-semibold text-[#007A8A]">+ 18% GST</div>
          <div className="text-[11px] text-slate-500 mt-1">{L("Classroom + lab assistance", "क्लासरूम + लैब सहायता")}</div>
        </button>
      ))}
    </div>
  );
};

const ManualCoupon = ({ form, onApplied }) => {
  const { L } = useLang();
  const [code, setCode] = useState("");
  const apply = async () => {
    try {
      const { data } = await api.post("/coupons/validate", { code, email: form.email, class_grade: form.class_grade });
      onApplied(data); toast.success(L(`${data.discount_pct}% off applied`, `${data.discount_pct}% छूट लागू`));
    } catch (e) { toast.error(errText(e.response?.data?.detail, L("Invalid coupon", "अमान्य कूपन"))); }
  };
  return (
    <div className="flex gap-2">
      <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder={L("Have a code? e.g. Robo-A-100", "कोड है? जैसे Robo-A-100")} className="bg-white h-11" data-testid="coupon-manual-input" />
      <Button type="button" variant="outline" onClick={apply} disabled={code.length < 6 || !form.email} className="h-11 rounded-full" data-testid="coupon-apply-btn">{L("Apply", "लागू करें")}</Button>
    </div>
  );
};

export default function StudentEnrollment() {
  const { L } = useLang();
  const nav = useNavigate();
  const cfg = usePaymentConfig();
  const [method, setMethod] = useState("razorpay");
  useEffect(() => { if (cfg.razorpay_enabled) setMethod("razorpay"); }, [cfg.razorpay_enabled]);
  const [params] = useSearchParams();
  const initProg = PROGRAMS.includes(params.get("program")) ? params.get("program") : "Robotics";
  const [f, setF] = useState({ student_name: "", parent_name: "", email: "", phone: "", class_grade: "8", school_name: "", city: "", state: "", program: initProg, leaderboard_opt_in: false });
  const [coupon, setCoupon] = useState(null);
  const [refCode] = useState(() => takeRef(params));
  const [stats, reloadStats] = useCouponStats();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const on = (k) => (e) => { setF({ ...f, [k]: e.target.value }); if (k === "email") setCoupon(null); };
  const set = (k) => (v) => { setF({ ...f, [k]: v }); if (k === "class_grade" && coupon && sectionFor(v) !== coupon.section) setCoupon(null); };
  const fee = feeFor(f.class_grade);
  const paid = useCallback((data) => nav(`/payment/success?session_id=${data.session_id}`), [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = { ...f, plan: "annual", coupon_code: coupon?.code || "", ref_code: refCode, origin_url: window.location.origin };
    try {
      const { data } = await api.post("/razorpay/enrollment", payload);
      setOrder(data); setLoading(false);
    } catch (err) {
      toast.error(errText(err.response?.data?.detail, L("Could not start payment.", "भुगतान शुरू नहीं हो सका।")));
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-4rem)]" data-testid="enroll-page">
      <div className="relative hidden lg:block bg-[#F0F4F8]">
        <img src={IMG.hero} alt="Students learning robotics" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#E8F0FE] via-[#E8F0FE]/70 to-transparent" />
        <div className="absolute bottom-0 p-12 text-[#0A192F]">
          <span className="rs-chip">{L("Admissions & Pricing", "दाखिला और फ़ीस")}</span>
          <h1 className="text-4xl lg:text-5xl font-extrabold mt-4 leading-tight" data-testid="enroll-tagline">{L(...TAGLINE)}</h1>
          <p className="text-[#007A8A] font-semibold mt-2">{L(...SUPPORT_LINE)}</p>
          <p className="text-slate-600 mt-3 max-w-md">{L("Your child's first robot is one form away. Pick the class, grab a coupon and pay securely.", "आपके बच्चे का पहला रोबोट बस एक फ़ॉर्म दूर है। कक्षा चुनें, कूपन लें और सुरक्षित भुगतान करें।")}</p>
        </div>
      </div>
      <div className="bg-[#E8F0FE] p-6 md:p-12">
        <div className="lg:hidden mb-6">
          <span className="rs-chip">{L("Admissions & Pricing", "दाखिला और फ़ीस")}</span>
          <h1 className="text-3xl font-extrabold text-[#0A192F] mt-3" data-testid="enroll-tagline-mobile">{L(...TAGLINE)}</h1>
        </div>
        <form onSubmit={submit} className="space-y-8 max-w-xl" data-testid="enroll-form">
          {refCode && <p className="flex items-center gap-2 text-sm font-semibold text-[#0A192F] bg-white border border-[#FFD166] rounded-xl px-4 py-2.5" data-testid="enroll-referral-note"><Gift className="w-4 h-4 text-[#FF2E63]" />{L("A friend invited you! Grab your own 50% or 70% coupon below.", "एक दोस्त ने आपको बुलाया है! नीचे अपना 50% या 70% कूपन लें।")}</p>}
          <div>
            <h2 className="text-lg font-bold text-[#0A192F] mb-1">{L("1. Annual fee by class", "1. कक्षा अनुसार वार्षिक फ़ीस")}</h2>
            <p className="text-xs text-[#B42318] mb-3" data-testid="enroll-kits-note">{L(...KITS_NOTE)}</p>
            <FeeCards cls={f.class_grade} onPick={set("class_grade")} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#0A192F] mb-3">{L("2. Student details", "2. छात्र विवरण")}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field id="student_name" label={L("Student name", "छात्र का नाम")} required value={f.student_name} onChange={on("student_name")} />
              <Field id="parent_name" label={L("Parent / Guardian", "अभिभावक")} required value={f.parent_name} onChange={on("parent_name")} />
              <Field id="email" label={L("Email", "ईमेल")} type="email" required value={f.email} onChange={on("email")} />
              <Field id="phone" label={L("Phone (WhatsApp)", "फ़ोन (WhatsApp)")} required value={f.phone} onChange={on("phone")} />
              <Pick id="class" label={L("Class", "कक्षा")} value={f.class_grade} onChange={set("class_grade")} options={CLASSES.map((c) => ({ v: c, l: L(`Class ${c}`, `कक्षा ${c}`) }))} />
              <Pick id="program" label={L("Program", "कार्यक्रम")} value={f.program} onChange={set("program")} options={PROGRAMS.map((p) => ({ v: p, l: L(p, PROGRAM_HI[p]) }))} />
              <Field id="school_name" label={L("School", "स्कूल")} required value={f.school_name} onChange={on("school_name")} />
              <Field id="city" label={L("City", "शहर")} required value={f.city} onChange={on("city")} />
              <Field id="state" label={L("State", "राज्य")} required value={f.state} onChange={on("state")} />
            </div>
          </div>
          <div className="space-y-3">
            <h2 className="text-lg font-bold text-[#0A192F] flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#FF2E63]" />{L("3. Coupon (new admissions, till ", "3. कूपन (नए दाखिले, ")}{L(...OFFER_END_LABEL)})</h2>
            <CouponCounters stats={stats} active={sectionFor(f.class_grade)} />
            <CouponBox form={f} coupon={coupon} setCoupon={setCoupon} onStatsChange={reloadStats} />
            {!coupon && <ManualCoupon form={f} onApplied={setCoupon} />}
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#0A192F] mb-3">{L("4. Bill", "4. बिल")}</h2>
            <BillSummary fee={fee} pct={coupon?.discount_pct || 0} label={L(`Class ${f.class_grade} annual fee`, `कक्षा ${f.class_grade} वार्षिक फ़ीस`)} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#0A192F] mb-3">{L("5. Payment method", "5. भुगतान का तरीका")}</h2>
            <PaymentMethodPicker value={method} onChange={setMethod} razorpayEnabled={cfg.razorpay_enabled} />
          </div>
          <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer" data-testid="enroll-board-optin">
            <Checkbox checked={f.leaderboard_opt_in} onCheckedChange={(v) => setF({ ...f, leaderboard_opt_in: !!v })} className="mt-0.5" data-testid="enroll-board-optin-checkbox" />
            <span>{L("Show our family on the monthly Top Ambassadors board if friends join through our link (first name, last initial and city only).", "अगर दोस्त हमारे लिंक से जुड़ें तो हमारे परिवार को मासिक टॉप एंबेसडर बोर्ड पर दिखाएँ (केवल पहला नाम, उपनाम का पहला अक्षर और शहर)।")}</span>
          </label>
          <Button type="submit" disabled={loading || !cfg.razorpay_enabled} className="w-full rs-cta h-12 rounded-full text-base" data-testid="enroll-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `${L("Proceed to Payment", "भुगतान करें")} · ${fmtINR(payable(fee, coupon?.discount_pct || 0))}`}
          </Button>
          <p className="text-xs text-slate-500 flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> {L("Secure Razorpay — UPI QR, cards, netbanking. Login ID and password arrive by WhatsApp, SMS and email.", "सुरक्षित Razorpay — UPI QR, कार्ड, नेटबैंकिंग। लॉगिन ID और पासवर्ड WhatsApp, SMS और ईमेल पर मिलेगा।")}</p>
        </form>
      </div>
      <RazorpayPay order={order} description={`${f.program} admission - ${f.student_name}`} onPaid={paid} onClose={() => setOrder(null)} />
    </div>
  );
}
